import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { addDoc, collection, doc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { isMobile } from 'react-device-detect';
import TextField from '@mui/material/TextField';
import Button from '../Button/Button';
import SmallPop from '../pops/SmallPop';
import { captureElementAsImage, saveImageBlob } from '../../functions/imageCaptureUtils';
import { ReactComponent as UndoIcon } from '../../../assets/icons/svg/undo-left-round-svgrepo-com.svg';
import { ReactComponent as ShareIcon } from '../../../assets/icons/svg/share-svgrepo-com.svg';
import { ReactComponent as UploadIcon } from '../../../assets/icons/svg/upload-minimalistic-svgrepo-com.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/svg/download-minimalistic-svgrepo-com.svg';
import { ReactComponent as UserIcon } from '../../../assets/icons/svg/user-svgrepo-com.svg';
import './CupComplete.css';

/**
 * Shared cup completion component for both mk8dx and world game modes.
 * Handles cup naming, sharing, downloading, and uploading to Firestore.
 * 
 * @param {Array} cup - The completed cup data
 * @param {ReactNode} cupDisplay - The visual cup display component
 * @param {Function} onReset - Callback to reset cup builder
 * @param {Function} formatCupForFirestore - Function to format cup data for Firestore (returns {tracks, trackList, ...otherFields})
 * @param {Function} formatCupText - Function to format cup as text for sharing
 * @param {string} firestoreCollection - Firestore collection name ("cups" or "worldCups")
 * @param {string} userCupsField - User document field name ("cups" or "worldCups")
 * @param {string} gameLabel - Label for the game (e.g., "#MK8DX" or "Mario Kart World")
 * @param {string} shareUrl - URL to include in shared text
 * @param {string} defaultImageName - Default filename for downloaded image
 * @param {Object} signInState - State to pass when navigating to sign in (optional)
 * @param {string} variant - Button style variant ('mk8dx' or 'world')
 * @param {string} color - Button color preset (optional, defaults based on variant)
 */
export default function CupComplete({
    cup,
    cupDisplay,
    onReset,
    formatCupForFirestore,
    formatCupText,
    firestoreCollection,
    userCupsField,
    gameLabel,
    shareUrl,
    defaultImageName,
    signInState = null,
    variant = 'mk8dx',
    color
}) {
    const navigate = useNavigate();
    const { firestore, auth } = useFirebase();

    const [cupName, setCupName] = useState("");
    const [upload, setUpload] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [smallPop, setSmallPop] = useState(null);

    const cupContainerRef = useRef(null);

    const buttonColor = color || (variant === 'world' ? 'green' : 'red');

    /**
     * Uploads the user's created cup to Firestore database.
     */
    async function fbUpload() {
        setUpload(true);

        try {
            // Get formatted data from the provided formatter function
            const formattedData = formatCupForFirestore(cup, cupName);

            // Upload cup document
            const newDoc = await addDoc(collection(firestore, firestoreCollection), {
                creator: auth.currentUser.uid,
                timestamp: serverTimestamp(),
                cupName: cupName,
                ...formattedData
            });

            console.log(newDoc.id);

            // Update user document with new cup reference
            const userdoc = doc(firestore, "users", auth.currentUser.uid);
            await updateDoc(userdoc, { [userCupsField]: arrayUnion(newDoc) });

            // Determine browse path based on game variant
            const browsePath = variant === 'mk8dx'
                ? `/mk8dx/browse/cup/${newDoc.id}`
                : `/browse/cup/${newDoc.id}`;

            setSmallPop({
                text: "Cup uploaded",
                link: {
                    to: browsePath,
                    text: "View Cup Page"
                },
            });
        } catch (error) {
            setSmallPop({ text: `There was an error posting your cup: ${error.message}` });
            setUpload(false);
        }
    }

    const handleCopyText = () => {
        const text = `My Custom ${gameLabel} Cup\n${cupName ? cupName + "\n" : ""}${formatCupText(cup)}\n${shareUrl}`;
        navigator.clipboard.writeText(text);
        setSmallPop({ text: "Copied cup to clipboard!" });
    };

    const handleDownloadImage = async () => {
        if (!cupContainerRef.current) return;

        setDownloading(true);

        try {
            const blob = await captureElementAsImage(cupContainerRef.current);
            const fileName = `${cupName || defaultImageName}.png`;
            const result = await saveImageBlob(blob, fileName, gameLabel, cupName);

            if (result.success) {
                // Show success message
            } else if (result.method === 'abort') {
                // User canceled - don't show error
            }
        } catch (error) {
            // Show error message
        } finally {
            setDownloading(false);
        }
    };

    const handleRestart = () => {
        onReset();
        setUpload(false);
        window.scrollTo(0, 0);
    };

    return (
        <div className="cup-complete-container">
            <h2>Cup Complete</h2>

            <h3 style={{ textAlign: "center", marginBottom: 0 }}>
                Name your cup! (optional)
            </h3>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                <TextField
                    label="Cup Name"
                    placeholder="Enter Cup Name"
                    value={cupName}
                    onChange={(e) => setCupName(e.target.value)}
                    variant="outlined"
                    fullWidth
                    style={{ maxWidth: '500px' }}
                />
            </div>

            {/* Render the provided cup display component */}
            <div id="cup-container" ref={cupContainerRef}>
                {cupDisplay()}
            </div>

            <div className="cup-complete-buttons">
                <Button
                    text="Restart"
                    icon={UndoIcon}
                    onClick={handleRestart}
                    variant={variant}
                    color={buttonColor}
                />

                <Button
                    text="Copy Text"
                    icon={ShareIcon}
                    onClick={handleCopyText}
                    variant={variant}
                    color={buttonColor}
                />

                {!isMobile && <Button
                    text={downloading ? "Processing..." : "Save Image"}
                    icon={DownloadIcon}
                    onClick={handleDownloadImage}
                    disabled={downloading}
                    variant={variant}
                    color={buttonColor}
                />}

                {auth?.currentUser?.uid ? (
                    <Button
                        text="Post Cup"
                        icon={UploadIcon}
                        onClick={fbUpload}
                        disabled={upload}
                        variant={variant}
                        color={buttonColor}
                    />
                ) : (
                    <Button
                        text="Sign in to post"
                        icon={UserIcon}
                        onClick={() => navigate("/signin", { state: signInState })}
                        variant={variant}
                        color={buttonColor}
                    />
                )}
            </div>

            {smallPop && (
                <SmallPop
                    text={smallPop.text}
                    link={smallPop.link}
                    onClose={() => setSmallPop(null)}
                />
            )}
        </div>
    );
}